import React, { useState } from "react";
import {
    User,
    Mail,
    Phone,
    Building2,
    BookOpen,
    Users,
    GraduationCap,
    Loader2,
    CheckCircle2,
    ArrowRight,
} from "lucide-react";
import { API_BASE_URL } from "../../config/apiConfig";

const inputClass =
    "w-full border border-slate-200 bg-white rounded-lg pl-10 pr-4 py-3 text-sm text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-[#2148C0]/30 focus:border-[#2148C0] transition";

function Field({ icon: Icon, children }) {
    return (
        <div className="relative">
            <div className="absolute left-3 top-1/2 -translate-y-1/2 text-[#2148C0]/60 pointer-events-none">
                <Icon size={16} />
            </div>
            {children}
        </div>
    );
}

const ROLES = [
    { value: "student", label: "Student", icon: BookOpen },
    { value: "parent", label: "Parent", icon: Users },
    { value: "teacher", label: "Teacher", icon: GraduationCap },
];

const WebinarRegistrationPage = () => {
    const [formData, setFormData] = useState({
        fullName: "",
        email: "",
        phone: "",
        school: "",
        role: "",
    });
    const [loading, setLoading] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const [error, setError] = useState("");

    const handleChange = (e) =>
        setFormData({ ...formData, [e.target.name]: e.target.value });

    const handleRoleSelect = (role) => {
        setFormData({ ...formData, role });
        setError("");
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError("");

        if (!formData.role) {
            setError("Please select whether you're a student, parent, or teacher.");
            return;
        }

        setLoading(true);
        try {
            const res = await fetch(`${API_BASE_URL}/api/webinar`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(formData),
            });

            let data = {};
            try {
                data = await res.json();
            } catch {
                // response wasn't valid JSON (e.g. a proxy/404 error page)
            }

            if (!res.ok) throw new Error(data.message || "Something went wrong. Please try again.");
            setSubmitted(true);
        } catch (err) {
            setError(err.message || "Something went wrong. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-gradient-to-br from-[#f8f7ff] via-[#eef2ff] to-[#f0f4ff] flex items-center justify-center px-4 py-16">
            <div className="w-full max-w-lg">
                <div className="text-center mb-8">
                    <span className="inline-flex items-center gap-2 bg-blue-100 text-[#2148C0] px-4 py-1.5 rounded-full text-xs font-semibold uppercase tracking-wide">
                        Free Webinar
                    </span>
                    <h1 className="text-3xl md:text-4xl font-bold text-slate-800 mt-4 mb-3">
                        Reserve Your Spot at the{" "}
                        <span className="bg-gradient-to-r from-[#2148C0] to-purple-600 bg-clip-text text-transparent">
                            TeeChaa Webinar
                        </span>
                    </h1>
                    <p className="text-slate-500 text-sm md:text-base">
                        Register below and we'll email you the details — free for students, parents, and teachers.
                    </p>
                </div>

                {submitted ? (
                    <div className="bg-white rounded-2xl shadow-xl p-8 md:p-10 text-center border border-slate-100">
                        <div className="mx-auto mb-5 flex items-center justify-center rounded-full w-14 h-14 bg-green-50 border border-green-200">
                            <CheckCircle2 size={28} className="text-green-600" />
                        </div>
                        <h2 className="text-xl font-bold text-slate-800 mb-2">You're registered!</h2>
                        <p className="text-slate-500 text-sm">
                            Check your inbox — we've sent a confirmation email to{" "}
                            <strong>{formData.email}</strong> with what to expect next.
                        </p>
                    </div>
                ) : (
                    <form
                        onSubmit={handleSubmit}
                        className="bg-white rounded-2xl shadow-xl p-6 md:p-8 space-y-4 border border-slate-100"
                    >
                        <Field icon={User}>
                            <input
                                name="fullName"
                                placeholder="Full name"
                                required
                                value={formData.fullName}
                                onChange={handleChange}
                                className={inputClass}
                            />
                        </Field>

                        <Field icon={Mail}>
                            <input
                                type="email"
                                name="email"
                                placeholder="Email address"
                                required
                                value={formData.email}
                                onChange={handleChange}
                                className={inputClass}
                            />
                        </Field>

                        <div className="grid grid-cols-2 gap-3">
                            <Field icon={Phone}>
                                <input
                                    type="tel"
                                    name="phone"
                                    placeholder="Phone number"
                                    required
                                    value={formData.phone}
                                    onChange={handleChange}
                                    className={inputClass}
                                />
                            </Field>

                            <Field icon={Building2}>
                                <input
                                    name="school"
                                    placeholder="School"
                                    required
                                    value={formData.school}
                                    onChange={handleChange}
                                    className={inputClass}
                                />
                            </Field>
                        </div>

                        <div>
                            <p className="text-sm font-medium text-slate-700 mb-2">I am a...</p>
                            <div className="grid grid-cols-3 gap-3">
                                {ROLES.map(({ value, label, icon: Icon }) => {
                                    const selected = formData.role === value;
                                    return (
                                        <button
                                            key={value}
                                            type="button"
                                            onClick={() => handleRoleSelect(value)}
                                            className={`flex flex-col items-center justify-center gap-2 py-4 rounded-xl border-2 transition-all ${selected
                                                    ? "border-[#2148C0] bg-[#2148C0]/5 text-[#2148C0]"
                                                    : "border-slate-200 text-slate-500 hover:border-slate-300"
                                                }`}
                                        >
                                            <Icon size={22} />
                                            <span className="text-xs font-semibold">{label}</span>
                                        </button>
                                    );
                                })}
                            </div>
                        </div>

                        {error && (
                            <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-4 py-2.5">
                                {error}
                            </div>
                        )}

                        <button
                            type="submit"
                            disabled={loading}
                            className={`w-full flex items-center justify-center gap-2 text-white font-semibold py-3.5 rounded-xl transition-all ${loading
                                    ? "opacity-70 cursor-not-allowed"
                                    : "hover:shadow-lg hover:-translate-y-0.5"
                                }`}
                            style={{ background: "linear-gradient(135deg, #2148C0, #6C63FF)" }}
                        >
                            {loading ? (
                                <>
                                    <Loader2 size={16} className="animate-spin" />
                                    Registering...
                                </>
                            ) : (
                                <>
                                    Register for the Webinar
                                    <ArrowRight size={16} />
                                </>
                            )}
                        </button>

                        <p className="text-center text-slate-400 text-xs">
                            No spam. We'll only email you about this webinar.
                        </p>
                    </form>
                )}
            </div>
        </div>
    );
};

export default WebinarRegistrationPage;
